import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { fmtPp, fmtSignedPct } from "../format";

export interface ConversionRate {
  key: string;
  label: string;
  /** Current value as a fraction (0.249 = 24.9%). */
  value: number | null;
  /** Prior-period value as a fraction. */
  prev: number | null;
  /** Prior-year value as a fraction. */
  yoy: number | null;
  /** Whether higher is better. */
  goodDirection: "up" | "down";
  /** SQO→Won only: Min/Max range as still-open deals resolve, plus counts. */
  min?: number | null;
  max?: number | null;
  won?: number;
  closed?: number;
  stillOpen?: number;
  total?: number;
}

export const CONVERSION_PLACEHOLDERS: ConversionRate[] = [
  { key: "new_to_sqo", label: "New → SQO", value: null, prev: null, yoy: null, goodDirection: "up" },
  { key: "sqo_to_won", label: "SQO → Won", value: null, prev: null, yoy: null, goodDirection: "up" },
  { key: "won_to_billed", label: "Won → Billed", value: null, prev: null, yoy: null, goodDirection: "up" },
  { key: "churn_rate", label: "Churn Rate", value: null, prev: null, yoy: null, goodDirection: "down" },
  { key: "contraction_rate", label: "Contraction Rate", value: null, prev: null, yoy: null, goodDirection: "down" },
];

export function ConversionRateTrends({
  rates,
  source,
}: {
  rates: ConversionRate[] | null;
  source?: string;
}) {
  const items = rates ?? CONVERSION_PLACEHOLDERS;
  return (
    <Card className="col-span-4">
      <CardHeader className="pb-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Conversion Rate Trends — vs. Prior Period &amp; Prior Year
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {items.map((r) => (
            <ConversionTile key={r.key} rate={r} />
          ))}
        </div>
        <div className="mt-4 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/50">
          {source ?? "Source TBD"}
        </div>
      </CardContent>
    </Card>
  );
}

function ConversionTile({ rate }: { rate: ConversionRate }) {
  const dPrev =
    rate.value != null && rate.prev != null ? rate.value - rate.prev : null;
  const dYoy =
    rate.value != null && rate.yoy != null ? rate.value - rate.yoy : null;

  const direction = (delta: number | null) =>
    delta == null
      ? "neutral"
      : (rate.goodDirection === "up" ? delta >= 0 : delta <= 0)
        ? "good"
        : "bad";

  // The range variant (SQO→Won) has no prior to compare against, so tone it by
  // the win-rate value itself: > 20% green, 15–20% amber, < 15% red.
  const hasRange = rate.min != null && rate.max != null;
  const rangeTone =
    rate.value == null
      ? "neutral"
      : rate.value > 0.2
        ? "good"
        : rate.value >= 0.15
          ? "warn"
          : "bad";
  const headlineDir = hasRange ? rangeTone : direction(dPrev);
  const headlineClass =
    headlineDir === "good"
      ? "text-success"
      : headlineDir === "warn"
        ? "text-warning"
        : headlineDir === "bad"
          ? "text-danger"
          : "text-muted-foreground";
  const headlineBar =
    headlineDir === "good"
      ? "bg-success"
      : headlineDir === "warn"
        ? "bg-warning"
        : headlineDir === "bad"
          ? "bg-danger"
          : "bg-muted";

  return (
    <div className="relative overflow-hidden rounded-md border border-border bg-surface-raised/50 p-3">
      <div className={cn("absolute left-0 top-0 h-full w-[3px]", headlineBar)} />
      <div className="text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">
        {rate.label}
      </div>
      <div
        className={cn(
          "mt-1 text-2xl font-extrabold tracking-tight tabular-nums",
          headlineClass,
        )}
      >
        {rate.value != null ? fmtSignedPct(rate.value * 100, 1).replace("+", "") : "—"}
      </div>
      {rate.min != null && rate.max != null ? (
        <div className="mt-2 flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-[10px]">
            <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 font-semibold text-muted-foreground">
              {(rate.min * 100).toFixed(1)}%–{(rate.max * 100).toFixed(1)}%
            </span>
            <span className="text-muted-foreground">min–max</span>
          </div>
          <div className="text-[9.5px] text-muted-foreground">
            {rate.won ?? 0} won / {rate.closed ?? 0} closed · {rate.stillOpen ?? 0} open
          </div>
        </div>
      ) : (
        <div className="mt-2 flex flex-col gap-1">
          <DeltaPill delta={dPrev} dir={direction(dPrev)} suffix="vs prior" />
          <DeltaPill delta={dYoy} dir={direction(dYoy)} suffix="vs last yr" />
        </div>
      )}
    </div>
  );
}

function DeltaPill({
  delta,
  dir,
  suffix,
}: {
  delta: number | null;
  dir: "good" | "bad" | "neutral";
  suffix: string;
}) {
  const bg =
    dir === "good"
      ? "bg-success-light text-success"
      : dir === "bad"
        ? "bg-danger-light text-danger"
        : "bg-muted text-muted-foreground";
  return (
    <span className="flex items-center gap-1.5 text-[10px]">
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-semibold",
          bg,
        )}
      >
        {delta == null ? "—" : delta >= 0 ? "↑" : "↓"} {delta == null ? "" : fmtPp(delta)}
      </span>
      <span className="text-muted-foreground">{suffix}</span>
    </span>
  );
}
